import { useState, useEffect, useRef } from "react";
import { Zap, X } from "lucide-react";
import { TAP } from "../lib/haptica";

const CORNER_KEY = "mipae:revisaFab:corner";
const TOOLTIP_KEY = "mipae:revisaFab:tooltipSeen";
const FAB_SIZE = 52;
const DELETE_ZONE_SIZE = 64;
const DRAG_THRESHOLD = 6;
const SAFE_MARGIN = 12;
const CORNER_INSET = 20;
const DELETE_ZONE_BOTTOM_OFFSET = 96;
const LONGPRESS_MS = 320;
const SNAP_MS = 220;

const CORNERS = ["tl", "tr", "bl", "br"];

function loadCorner() {
  try {
    const raw = localStorage.getItem(CORNER_KEY);
    if (CORNERS.includes(raw)) return raw;
  } catch { /* ignore */ }
  return "br";
}

function saveCorner(c) {
  try { localStorage.setItem(CORNER_KEY, c); } catch { /* ignore */ }
}

function cornerToPos(corner) {
  if (typeof window === "undefined") return { x: 0, y: 0 };
  const w = window.innerWidth;
  const h = window.innerHeight;
  const left = CORNER_INSET;
  const right = w - FAB_SIZE - CORNER_INSET;
  const top = CORNER_INSET;
  // Match the previous default of `bottom-20 right-20` (~80 px from bottom)
  const bottom = h - FAB_SIZE - 80;
  switch (corner) {
    case "tl": return { x: left, y: top };
    case "tr": return { x: right, y: top };
    case "bl": return { x: left, y: bottom };
    case "br":
    default:   return { x: right, y: bottom };
  }
}

function nearestCorner(pos) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const cx = pos.x + FAB_SIZE / 2;
  const cy = pos.y + FAB_SIZE / 2;
  const horiz = cx < w / 2 ? "l" : "r";
  const vert = cy < h / 2 ? "t" : "b";
  return `${vert}${horiz}`;
}

function clampPos(pos) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return {
    x: Math.max(SAFE_MARGIN, Math.min(pos.x, w - FAB_SIZE - SAFE_MARGIN)),
    y: Math.max(SAFE_MARGIN, Math.min(pos.y, h - FAB_SIZE - SAFE_MARGIN)),
  };
}

function getDeleteCenter() {
  return {
    x: window.innerWidth / 2,
    y: window.innerHeight - DELETE_ZONE_BOTTOM_OFFSET,
  };
}

function isOverDelete(clientX, clientY) {
  const c = getDeleteCenter();
  return Math.hypot(clientX - c.x, clientY - c.y) <= DELETE_ZONE_SIZE / 2 + FAB_SIZE / 4;
}

export default function RevisaFab({ active, justRestored, onOpen, onDismiss }) {
  const [corner, setCorner] = useState(() => loadCorner());
  const [pos, setPos] = useState(() => cornerToPos(loadCorner()));
  const [dragging, setDragging] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const [overDelete, setOverDelete] = useState(false);
  const [showTooltip, setShowTooltip] = useState(() => {
    try { return localStorage.getItem(TOOLTIP_KEY) !== "1"; } catch { return false; }
  });
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const dragRef = useRef({
    pointerId: null,
    offsetX: 0,
    offsetY: 0,
    startX: 0,
    startY: 0,
    moved: false,
  });
  const longPressTimerRef = useRef(null);
  const tooltipTimerRef = useRef(null);

  // Re-derive position from corner on resize
  useEffect(() => {
    const onResize = () => { if (!dragging) setPos(cornerToPos(corner)); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [corner, dragging]);

  // Reveal tooltip after 1s, hide after 6s; dismiss on first FAB interaction
  useEffect(() => {
    if (!showTooltip) return undefined;
    const showT = setTimeout(() => setTooltipVisible(true), 1000);
    const hideT = setTimeout(() => dismissTooltip(), 7000);
    tooltipTimerRef.current = { showT, hideT };
    return () => {
      clearTimeout(showT);
      clearTimeout(hideT);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTooltip]);

  const dismissTooltip = () => {
    if (!showTooltip) return;
    try { localStorage.setItem(TOOLTIP_KEY, "1"); } catch { /* ignore */ }
    setTooltipVisible(false);
    setShowTooltip(false);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handlePointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dismissTooltip();
    const target = e.currentTarget;
    dragRef.current = {
      pointerId: e.pointerId,
      offsetX: e.clientX - pos.x,
      offsetY: e.clientY - pos.y,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      if (dragRef.current.pointerId !== e.pointerId) return;
      try { target.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      setDragging(true);
      TAP(); // vocabulario háptico central (lib/haptica): modo arrastre activado
    }, LONGPRESS_MS);
  };

  const handlePointerMove = (e) => {
    const d = dragRef.current;
    if (d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!dragging) {
      // Pre-longpress: a drag-distance move cancels the long-press timer
      // (treat as a scroll/swipe attempt, NOT as a drag).
      if (Math.hypot(dx, dy) >= DRAG_THRESHOLD) {
        cancelLongPress();
        d.pointerId = null;
        d.moved = true;
      }
      return;
    }

    d.moved = true;
    setPos(clampPos({ x: e.clientX - d.offsetX, y: e.clientY - d.offsetY }));
    setOverDelete(isOverDelete(e.clientX, e.clientY));
  };

  const finishDrag = (e, fromCancel) => {
    const d = dragRef.current;
    if (d.pointerId !== e.pointerId) return;
    cancelLongPress();
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }

    const wasDragging = dragging;
    const wasMoved = d.moved;
    d.pointerId = null;

    if (!wasDragging) {
      // No long-press fired. If pointer didn't move significantly → tap.
      if (!wasMoved && !fromCancel) onOpen();
      return;
    }

    if (!fromCancel && isOverDelete(e.clientX, e.clientY)) {
      setDragging(false);
      setOverDelete(false);
      onDismiss();
      return;
    }

    const target = nearestCorner(pos);
    setCorner(target);
    saveCorner(target);
    setSnapping(true);
    setPos(cornerToPos(target));
    setDragging(false);
    setOverDelete(false);
    setTimeout(() => setSnapping(false), SNAP_MS + 30);
  };

  const positionTransition = snapping
    ? `left ${SNAP_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1), top ${SNAP_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1)`
    : "";
  const baseTransition = "opacity 200ms ease, transform 150ms ease, background-color 150ms ease, box-shadow 150ms ease";
  const transition = positionTransition ? `${positionTransition}, ${baseTransition}` : baseTransition;

  const tooltipSide = corner.endsWith("r") ? "right" : "left";
  const tooltipOffsetY = corner.startsWith("t") ? FAB_SIZE + 12 : -44;
  const tooltipStyle = {
    top: pos.y + tooltipOffsetY,
    ...(tooltipSide === "right"
      ? { right: window.innerWidth - (pos.x + FAB_SIZE) }
      : { left: pos.x }),
  };

  return (
    <>
      <button
        type="button"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={(e) => finishDrag(e, false)}
        onPointerCancel={(e) => finishDrag(e, true)}
        className={`no-print fixed z-50 rounded-full bg-[var(--ui-inverse)] text-white flex items-center justify-center shadow-lg ${dragging ? 'cursor-grabbing scale-110 ring-4 ring-white/30' : 'cursor-pointer active:scale-95 hover:bg-[var(--ui-inverse-hover)]'} ${justRestored ? 'animate-fab-pop' : ''}`}
        style={{
          width: FAB_SIZE,
          height: FAB_SIZE,
          left: pos.x,
          top: pos.y,
          transition,
          opacity: (active || dragging) ? 1 : 0.35,
          touchAction: "none",
          userSelect: "none",
        }}
        aria-label="Revisa"
        title="Revisa · mantén presionado para mover, suelta sobre la X para ocultar"
      >
        <Zap size={22} strokeWidth={1.75} />
      </button>

      {showTooltip && tooltipVisible && !dragging && (
        <div
          className="no-print fixed z-50 bg-[var(--ui-inverse)] text-white text-xs font-sans px-3 py-1.5 rounded-lg shadow-lg animate-fade-in pointer-events-none whitespace-nowrap"
          style={tooltipStyle}
          aria-hidden="true"
        >
          Mantén presionado para mover
        </div>
      )}

      {dragging && (
        <div
          className={`no-print fixed z-40 rounded-full flex items-center justify-center shadow-xl pointer-events-none transition-[transform,background-color] duration-150 ${overDelete ? 'bg-red-500 text-white scale-125' : 'bg-black/70 text-white scale-100'}`}
          style={{
            width: DELETE_ZONE_SIZE,
            height: DELETE_ZONE_SIZE,
            left: window.innerWidth / 2 - DELETE_ZONE_SIZE / 2,
            top: window.innerHeight - DELETE_ZONE_BOTTOM_OFFSET - DELETE_ZONE_SIZE / 2,
          }}
          aria-hidden="true"
        >
          <X size={28} strokeWidth={2} />
        </div>
      )}
    </>
  );
}
